import type { SocialOverview } from '@/social/types'

export const WEB_PUSH_CONVERSATION_HANDOFF_PARAM = 'pushHandoff'

const HANDOFF_DATABASE_NAME = 'mentiss-web-push-handoffs-v1'
const HANDOFF_DATABASE_VERSION = 1
const HANDOFF_STORE_NAME = 'conversation'
const HANDOFF_SCHEMA_VERSION = 1
const HANDOFF_TOKEN_PATTERN = /^[a-f0-9]{48}$/
const PRIVATE_IDENTIFIER_PATTERN = /^[\w-]{1,128}$/

export interface ConversationNotificationHandoffStore {
  take: (token: string) => Promise<unknown>
}

type ConversationNotificationHandoff = {
  schemaVersion: typeof HANDOFF_SCHEMA_VERSION
  token: string
  connectionId: string
  recipientUserId: string
  expiresAt: number
}

export type ConversationNotificationHandoffRequest = {
  present: boolean
  token: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validPrivateIdentifier(value: unknown): value is string {
  return typeof value === 'string' && PRIVATE_IDENTIFIER_PATTERN.test(value)
}

function parseHandoff(
  value: unknown,
  expectedToken: string,
): ConversationNotificationHandoff | null {
  if (!isRecord(value))
    return null
  if (
    value.schemaVersion !== HANDOFF_SCHEMA_VERSION
    || value.token !== expectedToken
    || !validPrivateIdentifier(value.connectionId)
    || !validPrivateIdentifier(value.recipientUserId)
    || typeof value.expiresAt !== 'number'
    || !Number.isFinite(value.expiresAt)
  ) {
    return null
  }
  return value as ConversationNotificationHandoff
}

export function conversationNotificationHandoffFromLocation(
  pathname: string,
  search: string,
): ConversationNotificationHandoffRequest {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  }
  catch {
    return { present: false, token: null }
  }
  const handoffValues = params.getAll(WEB_PUSH_CONVERSATION_HANDOFF_PARAM)
  const present = handoffValues.length > 0
  if (!present)
    return { present: false, token: null }

  const entries = [...params.entries()]
  const token = handoffValues.length === 1 ? handoffValues[0] : null
  if (
    pathname !== '/dashboard'
    || entries.length !== 2
    || params.get('tab') !== 'social'
    || token === null
    || !HANDOFF_TOKEN_PATTERN.test(token)
  ) {
    return { present: true, token: null }
  }
  return { present: true, token }
}

function openHandoffDatabase(
  databaseFactory: IDBFactory,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = databaseFactory.open(
      HANDOFF_DATABASE_NAME,
      HANDOFF_DATABASE_VERSION,
    )
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(HANDOFF_STORE_NAME)) {
        request.result.createObjectStore(HANDOFF_STORE_NAME, {
          keyPath: 'token',
        })
      }
    }
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close()
      resolve(request.result)
    }
    request.addEventListener('error', () =>
      reject(request.error ?? new Error('handoff-open')))
    request.onblocked = () => reject(new Error('handoff-blocked'))
  })
}

async function takeBrowserConversationNotificationHandoff(
  token: string,
): Promise<unknown> {
  if (typeof indexedDB === 'undefined')
    return null
  const database = await openHandoffDatabase(indexedDB)
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(HANDOFF_STORE_NAME, 'readwrite')
      const store = transaction.objectStore(HANDOFF_STORE_NAME)
      const readRequest = store.get(token)
      const cleanupRequest = store.openCursor()
      let value: unknown = null

      readRequest.onsuccess = () => {
        value = readRequest.result ?? null
        store.delete(token)
      }
      readRequest.addEventListener('error', () => transaction.abort())
      cleanupRequest.onsuccess = () => {
        const cursor = cleanupRequest.result
        if (!cursor)
          return
        const expiresAt = cursor.value?.expiresAt
        if (typeof expiresAt !== 'number' || expiresAt <= Date.now())
          cursor.delete()
        cursor.continue()
      }
      cleanupRequest.addEventListener('error', () => transaction.abort())
      transaction.oncomplete = () => resolve(value)
      transaction.addEventListener('error', () =>
        reject(transaction.error ?? new Error('handoff-transaction')))
      transaction.addEventListener('abort', () =>
        reject(transaction.error ?? new Error('handoff-aborted')))
    })
  }
  finally {
    database.close()
  }
}

const browserHandoffStore: ConversationNotificationHandoffStore = {
  take: takeBrowserConversationNotificationHandoff,
}

export async function consumeConversationNotificationHandoff({
  token,
  currentUserId,
  store = browserHandoffStore,
  now = Date.now,
}: {
  token: string
  currentUserId: string
  store?: ConversationNotificationHandoffStore
  now?: () => number
}): Promise<string | null> {
  if (
    !HANDOFF_TOKEN_PATTERN.test(token)
    || !validPrivateIdentifier(currentUserId)
  ) {
    return null
  }

  try {
    const handoff = parseHandoff(await store.take(token), token)
    if (
      !handoff
      || handoff.expiresAt <= now()
      || handoff.recipientUserId !== currentUserId
    ) {
      return null
    }
    return handoff.connectionId
  }
  catch {
    return null
  }
}

export function overviewAuthorizesConversation(
  overview: SocialOverview | undefined,
  connectionId: string,
  expectedUserId: string,
): boolean {
  return (
    !!overview
    && overview.profile.id === expectedUserId
    && (overview.administrator?.connectionId === connectionId
      || overview.administratorConversations.some(
        conversation => conversation.connectionId === connectionId,
      )
      || overview.friends.some(friend => friend.connectionId === connectionId))
  )
}

export async function authorizeConversationNotificationHandoff({
  token,
  currentUserId,
  loadAuthorizedOverview,
  store,
  now,
}: {
  token: string
  currentUserId: string
  loadAuthorizedOverview: (connectionId: string) => Promise<SocialOverview>
  store?: ConversationNotificationHandoffStore
  now?: () => number
}): Promise<string | null> {
  const connectionId = await consumeConversationNotificationHandoff({
    token,
    currentUserId,
    store,
    now,
  })
  if (!connectionId)
    return null

  let overview: SocialOverview
  try {
    overview = await loadAuthorizedOverview(connectionId)
  }
  catch {
    return null
  }
  return overviewAuthorizesConversation(overview, connectionId, currentUserId)
    ? connectionId
    : null
}
